import { Outlet, useLocation, useNavigate } from "react-router-dom"
import Sidebar from "../../components/sidebar/Sidebar"
import Navbar from "../../components/navbar/Navbar"
import { Suspense, useContext, useEffect } from "react"
import { navigation } from "../../utils/helper"
import LoadingSpinTrail from "../../components/loading/LoadingSpinTrail"
import { ErrorBoundary } from "react-error-boundary"
import ErrorFallback from "../../pages/errors/ErrorFallback"
import DataContext from "../../context/dataProvider"
import { useQuery } from "@tanstack/react-query"
import useUser from "../../hooks/useUser"
import { userService } from "../../services/userService"
import Footer from "../../components/footer"
import { authService } from "../../services/authService"

export default function MainLayout() {
  navigation.navigate = useNavigate()
  navigation.location = useLocation()
  const navigate = useNavigate()
  const user = useUser()
  const { setCurrentUser } = useContext(DataContext)

  // Redirect to login if no token or invalid user
  useEffect(() => {
    const token = localStorage.getItem("token")
    if (!token || user.id === 0) {
      authService.logout()
      navigate("/login", { replace: true })
    }
  }, [user.id, navigate])

  const { data: userData, error, isLoading } = useQuery(
    ["user", user.id],
    () => {
      const token = localStorage.getItem("token")
      if (!token || user.id === 0) {
        throw new Error("No valid token or user")
      }
      if (!user || !user.id) {
        console.warn('No user or user.id, skipping userService.getOne');
        return;
      }
      return userService.getOne(user.id)
    },
    {
      retry: false,
      enabled: user.id > 0 && !!localStorage.getItem("token"),
      onError: (error: Error) => {
        console.error("Error fetching user data:", error)
        if (error.message === "Invalid token" || error.message === "No token available" || error.message === "No valid token or user") {
          authService.logout()
          navigate("/login", { replace: true })
        }
      }
    }
  )

  useEffect(() => {
    if (userData) {
      setCurrentUser(userData)
    }
  }, [userData, setCurrentUser])

  // Show loading state while fetching user data
  if (isLoading) {
    return <LoadingSpinTrail />
  }

  // Handle error state
  if (error) {
    return <ErrorFallback error={error} resetErrorBoundary={() => {
      authService.logout()
      navigate("/login", { replace: true })
    }} />
  }

  return (
    <div className="flex w-full h-screen overflow-hidden">
      <Navbar />
      <div className="flex flex-1 h-[calc(100vh)]">
        <Sidebar />
        <div className="flex-1 flex flex-col bg-secondary-light h-full">
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<LoadingSpinTrail />}>
              <div className="h-full overflow-y-auto">
                <div className="w-full md:p-6 pt-12 mt-10">
                  <Outlet />
                </div>
                <div className="mt-4">
                  <Footer />
                </div>
              </div>
            </Suspense>
          </ErrorBoundary>
        </div>
      </div>
    </div>
  )
}